/**
 * The phone-line tab owns the Twilio invite. If that tab is in the background,
 * the incoming panel is shown on the tab the user is looking at. Answer and
 * decline are sent back to the phone-line tab.
 */
import { getVoiceTabId } from './voiceTabLeader';

const CHANNEL_NAME = 'rp-incoming-ui';
const VISIBLE_FRESH_MS = 3_000;

type VisibleNote = { at: number };

type IncomingHandlers = {
  show: (payload: unknown) => void;
  clearLocal: () => void;
  answer: () => void;
  decline: () => void;
};

let channel: BroadcastChannel | null = null;
let handlers: IncomingHandlers | null = null;
let started = false;
let pendingPayload: unknown = null;
let shownLocally = false;
const visibleTabs = new Map<string, VisibleNote>();

function isDocumentVisible(): boolean {
  return typeof document !== 'undefined' && document.visibilityState === 'visible';
}

function pickVisibleTab(): string | null {
  const self = getVoiceTabId();
  const now = Date.now();
  let bestId: string | null = null;
  let bestAt = 0;
  visibleTabs.forEach((note, tabId) => {
    if (tabId === self) return;
    if (now - note.at > VISIBLE_FRESH_MS) return;
    if (bestId === null || note.at > bestAt) {
      bestId = tabId;
      bestAt = note.at;
    }
  });
  return bestId;
}

function publishShow(to: string) {
  channel?.postMessage({ type: 'show', to, payload: pendingPayload });
}

function movePanelTo(tabId: string) {
  publishShow(tabId);
  if (shownLocally) {
    shownLocally = false;
    handlers?.clearLocal();
  }
}

export function startIncomingUiRelay(next: IncomingHandlers) {
  if (started || typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') {
    return;
  }
  started = true;
  handlers = next;
  const self = getVoiceTabId();
  channel = new BroadcastChannel(CHANNEL_NAME);
  channel.addEventListener('message', (event: MessageEvent) => {
    const data = event.data;
    if (!data?.type) return;

    if (data.type === 'visible') {
      if (!data.tabId || data.tabId === self) return;
      if (data.visible) {
        visibleTabs.set(data.tabId, { at: data.at || Date.now() });
        if (pendingPayload && !isDocumentVisible()) {
          movePanelTo(data.tabId);
        }
      } else {
        visibleTabs.delete(data.tabId);
      }
      return;
    }

    if (data.type === 'show' && data.to === self && isDocumentVisible()) {
      handlers?.show(data.payload);
      return;
    }
    if (data.type === 'clear') {
      handlers?.clearLocal();
      return;
    }
    if (data.type === 'answer') {
      handlers?.answer();
      return;
    }
    if (data.type === 'decline') {
      handlers?.decline();
    }
  });

  let wasVisible = isDocumentVisible();
  const announce = () => {
    const visible = isDocumentVisible();
    channel?.postMessage({ type: 'visible', tabId: self, visible, at: Date.now() });
    if (visible && !wasVisible && pendingPayload) {
      shownLocally = true;
      handlers?.show(pendingPayload);
      channel?.postMessage({ type: 'clear' });
    }
    wasVisible = visible;
  };
  document.addEventListener('visibilitychange', announce);
  window.setInterval(() => {
    if (isDocumentVisible()) announce();
  }, 1_000);
  announce();
}

/** Show the incoming panel here, or on the visible tab when this one is in the background. */
export function presentIncomingCall(payload: unknown) {
  pendingPayload = payload;
  if (isDocumentVisible() || !pickVisibleTab()) {
    shownLocally = true;
    handlers?.show(payload);
    return;
  }
  const target = pickVisibleTab();
  if (target) movePanelTo(target);
}

export function finishRemoteIncoming() {
  pendingPayload = null;
  shownLocally = false;
  channel?.postMessage({ type: 'clear' });
}

export function requestRemoteAnswer() {
  channel?.postMessage({ type: 'answer' });
}

export function requestRemoteDecline() {
  channel?.postMessage({ type: 'decline' });
}
